import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, DollarSign, Users, AlertCircle, ListOrdered, LogIn } from 'lucide-react';
import { Button, Badge } from '@/components/ui';
import type { SpecialEvent } from '@/types';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';

interface RegistrationCTAProps {
  event: SpecialEvent;
  isSoldOut?: boolean;
  onRegisterClick: () => void;
  onJoinWaitlistClick?: () => void;
  onLeaveWaitlistClick?: () => void;
  waitlistPosition?: number | null;
  isOnWaitlist?: boolean;
  isLoggedIn?: boolean;
  waitlistLoading?: boolean;
}

/**
 * Registration call-to-action section
 * Displays pricing, capacity, registration status, and prominent CTA button
 */
export const RegistrationCTA: FC<RegistrationCTAProps> = ({
  event,
  isSoldOut = false,
  onRegisterClick,
  onJoinWaitlistClick,
  onLeaveWaitlistClick,
  waitlistPosition: _waitlistPosition,
  isOnWaitlist = false,
  isLoggedIn = false,
  waitlistLoading = false,
}) => {
  // Format price from cents to dollars
  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Check registration availability
  const now = new Date();
  const registrationOpens = event.registrationOpensAt
    ? new Date(event.registrationOpensAt)
    : null;
  const registrationCloses = event.registrationClosesAt
    ? new Date(event.registrationClosesAt)
    : null;

  const isNotYetOpen = registrationOpens && now < registrationOpens;
  const isClosed = registrationCloses && now > registrationCloses;
  const isFull = isSoldOut || (event.maxCapacity && event.currentRegistrations >= event.maxCapacity);
  const isOpen = !isNotYetOpen && !isClosed && !isFull;

  // Calculate spots remaining
  const spotsRemaining = event.maxCapacity
    ? event.maxCapacity - event.currentRegistrations
    : null;

  // Format dates
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <section className="py-16 bg-gradient-to-b from-orange-600 to-orange-700">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-turquoise-800 text-white text-center py-8 px-6">
            <h2 className="text-4xl font-viking mb-2">Ready to Join Us?</h2>
            <p className="text-lg text-turquoise-200">Secure your spot at this special event</p>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Pricing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  <h3 className="font-bold text-turquoise-800">General Admission</h3>
                </div>
                <p className="text-3xl font-viking text-orange-600">
                  {formatPrice(event.ticketPrice)}
                </p>
                <p className="text-xs text-turquoise-600 mt-1">per person</p>
              </div>

              {event.eventRegistrantPrice !== null && (
                <div className="bg-turquoise-50 border border-turquoise-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-turquoise-600" />
                    <h3 className="font-bold text-turquoise-800">Event Registrants</h3>
                  </div>
                  <p className="text-3xl font-viking text-turquoise-600">
                    {event.eventRegistrantPrice === 0
                      ? 'FREE'
                      : formatPrice(event.eventRegistrantPrice)}
                  </p>
                  <p className="text-xs text-turquoise-600 mt-1">
                    {event.eventRegistrantPrice === 0
                      ? 'included with event registration'
                      : 'special rate for registrants'}
                  </p>
                </div>
              )}
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-3 justify-center">
              {isNotYetOpen && registrationOpens && (
                <Badge variant="warning" className="flex items-center gap-2 px-4 py-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  Opens {formatDate(registrationOpens.toISOString())}
                </Badge>
              )}

              {isClosed && (
                <Badge variant="destructive" className="flex items-center gap-2 px-4 py-2 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Registration Closed
                </Badge>
              )}

              {isFull && (
                <Badge variant="destructive" className="flex items-center gap-2 px-4 py-2 text-sm">
                  <Users className="h-4 w-4" />
                  Event Full
                </Badge>
              )}

              {isOpen && spotsRemaining !== null && spotsRemaining <= 10 && (
                <Badge variant="warning" className="flex items-center gap-2 px-4 py-2 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Only {spotsRemaining} spots left!
                </Badge>
              )}
            </div>

            {/* Capacity Info */}
            {event.maxCapacity && (
              <div className="text-center text-sm text-turquoise-600">
                <p>
                  {event.currentRegistrations} of {event.maxCapacity} spots filled
                </p>
              </div>
            )}

            {/* CTA Button */}
            <div className="pt-4">
              {isFull && !isNotYetOpen && !isClosed ? (
                // Waitlist UI when event is full/sold out
                <div className="space-y-3">
                  {isOnWaitlist ? (
                    <>
                      <div className="text-center p-4 bg-turquoise-50 border border-turquoise-200 rounded-lg">
                        <div className="flex items-center justify-center gap-2 text-turquoise-700 mb-1">
                          <ListOrdered className="h-5 w-5" />
                          <p className="font-bold">You're on the waitlist!</p>
                        </div>
                        <p className="text-xs text-turquoise-500 mt-1">
                          We'll notify you if a spot opens up
                        </p>
                      </div>
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full"
                        onClick={onLeaveWaitlistClick}
                        disabled={waitlistLoading}
                      >
                        Leave Waitlist
                      </Button>
                    </>
                  ) : REGISTRATIONS_CLOSED ? (
                    <ClosedNotice className="text-center" />
                  ) : isLoggedIn ? (
                    <Button
                      size="lg"
                      className="w-full text-lg py-6 font-bold shadow-lg hover:shadow-xl transition-all"
                      onClick={onJoinWaitlistClick}
                      disabled={waitlistLoading}
                    >
                      <ListOrdered className="h-5 w-5 mr-2" />
                      Join Waitlist
                    </Button>
                  ) : (
                    <div className="text-center space-y-2">
                      <Button
                        size="lg"
                        className="w-full text-lg py-6 font-bold shadow-lg hover:shadow-xl transition-all opacity-90"
                        disabled
                      >
                        Event Full
                      </Button>
                      <p className="text-sm text-turquoise-600 flex items-center justify-center gap-1">
                        <LogIn className="h-4 w-4" />
                        <Link to="/login" state={{ returnTo: '/special-event' }} className="underline hover:text-turquoise-800">Log in</Link> to join the waitlist
                      </p>
                    </div>
                  )}
                </div>
              ) : REGISTRATIONS_CLOSED ? (
                <ClosedNotice className="text-center text-turquoise-600" />
              ) : (
                <>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6 font-bold shadow-lg hover:shadow-xl transition-all"
                    onClick={onRegisterClick}
                    disabled={!isOpen}
                  >
                    {isNotYetOpen
                      ? 'Registration Opens Soon'
                      : isClosed
                      ? 'Registration Closed'
                      : !event.allowStandalonePurchase
                      ? 'Register for Event'
                      : 'Register Now'}
                  </Button>
                  {!event.allowStandalonePurchase && isOpen && (
                    <p className="text-center text-sm text-turquoise-600 mt-2">
                      Tickets available through event registration
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Registration window info */}
            {(registrationOpens || registrationCloses) && (
              <div className="pt-4 border-t border-turquoise-200 text-center text-sm text-turquoise-600">
                <p>
                  {registrationOpens && `Opens: ${formatDate(registrationOpens.toISOString())}`}
                  {registrationOpens && registrationCloses && ' • '}
                  {registrationCloses &&
                    `Closes: ${formatDate(registrationCloses.toISOString())}`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
